import React from "react";
import { useTranslation } from "react-i18next";

import Input from "@/components/input";
import { isApiError } from "@/queries/api/apiUtils";
import { notify } from "@/utils";
import { prepareImageForUpload, validateImageFile } from "@/utils/image-upload";
import { Logger } from "@/utils/logger";
import { OWNER_ROLES } from "@/constants/ownerOnboarding";
import { uploadOwnerPhoto } from "@/queries/api/ownerOnboardingApi";

import { useOwnerOnboarding } from "../-owner-onboarding-context";

interface Props {
  onContinue?: (handler: () => Promise<boolean>) => void;
}

const PHONE_RE = /^[+()\-\s0-9]{7,20}$/;

export function OwnerAboutYouStep({ onContinue }: Props) {
  const { t } = useTranslation("onboarding");
  const { data, patch, setOwnerPhotoUrl, saveDraft } = useOwnerOnboarding();
  const [uploading, setUploading] = React.useState(false);

  // Name + phone are pre-filled by OwnerOnboardingProvider so they're
  // populated regardless of which step the user lands on.

  React.useEffect(() => {
    if (!onContinue) return;
    onContinue(async () => {
      if (!data.ownerRole.trim()) {
        notify.error(t("ownerAboutYou.roleRequired"));
        return false;
      }
      if (!data.ownerName.trim()) {
        notify.error(t("ownerAboutYou.nameRequired"));
        return false;
      }
      if (!PHONE_RE.test(data.phone.trim())) {
        notify.error(t("ownerAboutYou.phoneInvalid"));
        return false;
      }
      try {
        await saveDraft();
        return true;
      } catch (e) {
        Logger.error("owner onboarding save failed at about-you", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(
          e instanceof Error ? e.message : t("ownerAboutYou.saveFailed"),
        );
        return false;
      }
    });
  }, [onContinue, data, saveDraft, t]);

  const handlePhoto = async (file: File) => {
    setUploading(true);
    try {
      let prepared: File;
      try {
        prepared = await prepareImageForUpload(file);
      } catch (e) {
        Logger.error("owner photo conversion failed", {
          error: e instanceof Error ? e.message : String(e),
        });
        notify.error(t("ownerAboutYou.photoTypeError"));
        return;
      }
      const valid = validateImageFile(prepared);
      if (!valid.ok) {
        notify.error(
          t(
            valid.reason === "size"
              ? "ownerAboutYou.photoTooLarge"
              : "ownerAboutYou.photoTypeError",
          ),
        );
        return;
      }
      const url = await uploadOwnerPhoto(prepared);
      setOwnerPhotoUrl(url);
      notify.success(t("ownerAboutYou.photoUploaded"));
    } catch (e) {
      Logger.error("owner photo upload failed", {
        error: e instanceof Error ? e.message : String(e),
        status: isApiError(e) ? e.status : undefined,
      });
      if (isApiError(e) && e.status === 413) {
        notify.error(t("ownerAboutYou.photoTooLargeServer"));
      } else {
        notify.error(t("ownerAboutYou.photoUploadFailed"));
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <section className="w-full flex flex-col gap-5">
      <div>
        <h2 className="heading-1">{t("ownerAboutYou.title")}</h2>
        <p className="body-small text-gray-600 mt-2">
          {t("ownerAboutYou.description")}
        </p>
      </div>
      <div className="flex flex-col gap-4 max-w-[480px]">
        <div className="flex flex-col gap-1">
          <label className="body-small text-gray-700" htmlFor="owner-role">
            {t("ownerAboutYou.yourRole")}
          </label>
          <select
            id="owner-role"
            value={data.ownerRole}
            onChange={(e) => patch({ ownerRole: e.currentTarget.value })}
            className="rounded-md border border-garnet/40 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-garnet"
          >
            <option value="">{t("ownerAboutYou.chooseRole")}</option>
            {OWNER_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <span className="body-small text-gray-700">
            {t("ownerAboutYou.profilePhoto")}
          </span>
          {data.ownerPhotoUrl ? (
            <img
              src={data.ownerPhotoUrl}
              alt={t("ownerAboutYou.profilePhotoAlt")}
              className="w-24 h-24 rounded-full object-cover ring-1 ring-garnet"
            />
          ) : null}
          <input
            type="file"
            accept="image/*"
            disabled={uploading}
            onChange={(e) => {
              const f = e.currentTarget.files?.[0];
              if (f) void handlePhoto(f);
              e.currentTarget.value = "";
            }}
          />
          {uploading && (
            <span className="body-small">{t("ownerAboutYou.uploading")}</span>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <Input
            label={t("ownerAboutYou.yourName")}
            value={data.ownerName}
            disabled
            readOnly
          />
          <span className="body-small text-gray-500">
            {t("ownerAboutYou.fromYourAccount")}
          </span>
        </div>
        <div className="flex flex-col gap-1">
          <Input
            label={t("ownerAboutYou.phone")}
            type="tel"
            value={data.phone}
            disabled
            readOnly
          />
          <span className="body-small text-gray-500">
            {t("ownerAboutYou.fromYourAccountVerified")}
          </span>
        </div>
      </div>
    </section>
  );
}
